import React, { useEffect } from 'react'
import { IonGrid, IonRow, IonCol, IonLabel, IonButton, IonItem, IonList } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SInput from '../../../components/stringInput/StringInput.component'
import { getPatientSearchState } from '../selectors/patientSearch.selector'
import {
  getPatientSearchResults,
  resetSearch,
  setDOB,
  setFirstName,
  setLastName,
  setSSN,
} from '../store/patientSearch.actions'
import { IPatientSearchState } from '../store/patientSearch.types'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { PatientSearchConstants } from '../store/patientSearch.constants'
import { transformDateWith4DigitYear } from '../../../utilities'
import { setSelectedOption } from '../../patientInformation/store/patientInformation.actions'
import { PatientInformationConstants } from '../../patientInformation/store/patientInformation.constants'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import Validation from '../../../components/validation/Validation.component'
import { isValidNemsisDOB } from '../../../utilities/dateTime.util'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import NewPatientButton from '../../../components/patientSearch/NewPatientButton.component'
import { setCurrentSubStep } from '../../pulsara/store/pulsara.actions'
import { PATIENT_MATCH_SUB_STEP_IDS } from '../../pulsara/constants'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'

const PatientSearchContent: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { isTablet } = useDeviceMetrics()
  const patientSearchState: IPatientSearchState = useSelector(getPatientSearchState)
  const serviceConfig: IServiceConfig = useSelector(getServiceConfigSelector)
  const { PR_PT_CRITERIA_SEARCH } = serviceConfig
  const { firstName, lastName, dOB, sSN } = patientSearchState
  const isOfflineStatus = useOfflineStatus()
  const isFromPulsara = history.location.pathname.includes('/pulsara')

  const onSearch = () => {
    dispatch(getPatientSearchResults())
    if (!isFromPulsara) {
      if (!isTablet) {
        history.push(TabConstants.PATIENT_SEARCH_RESULTS)
      } else {
        dispatch(setSelectedOption(PatientInformationConstants.PATIENT_SEARCH_RESULTS_OPTION))
      }
    } else {
      dispatch(setCurrentSubStep(PATIENT_MATCH_SUB_STEP_IDS.PATIENT_SEARCH_RESULTS))
    }
  }

  useEffect(() => {
    sSN && dispatch(setSSN(sSN))
    dOB && dispatch(setDOB(dOB))
  }, [])

  const onClear = () => {
    dispatch(setDOB(''))
    dispatch(setSSN(''))
    dispatch(setFirstName(''))
    dispatch(setLastName(''))
    dispatch(resetSearch())
  }

  const setFirstNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setFirstName(e?.detail?.value?.trim()))
  }

  const setLastNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setLastName(e?.detail?.value?.trim()))
  }

  const setDobOnChange = (numbers: string[]) => {
    let newDate = null
    if (numbers.length) {
      newDate = transformDateWith4DigitYear(numbers.join(''))
    }
    dispatch(setDOB(newDate))
  }

  const setSsnOnChange = (numbers: string[]) => {
    dispatch(setSSN(numbers.length ? numbers.join('') : null))
  }

  const getMinRequiredFieldsToSearch = () => {
    if (PR_PT_CRITERIA_SEARCH === 0) return 4
    else return PR_PT_CRITERIA_SEARCH
  }
  const hasMinReqFields = (): boolean => {
    let reqFieldFilledCount = 0
    if (firstName) reqFieldFilledCount++
    if (lastName) reqFieldFilledCount++
    if (dOB) reqFieldFilledCount++
    if (sSN) reqFieldFilledCount++
    if (reqFieldFilledCount >= getMinRequiredFieldsToSearch()) {
      if (sSN && sSN.length !== 4) return false
      if (dOB && !isValidNemsisDOB(dOB)) return false
      return true
    }
    return false
  }

  const infoTextItem = (
    <IonItem lines='none'>
      <IonLabel className='ion-text-wrap' color='medium'>
        <p>
          {t('patientSearch.minimumfieldsRequirementMessage1')} {getMinRequiredFieldsToSearch()}
          {t('patientSearch.minimumfieldsRequirementMessage2')}
        </p>
      </IonLabel>
    </IonItem>
  )

  const offlineInfoTextItem = (
    <IonItem color='light' lines='none'>
      <IonLabel className='ion-text-wrap'>
        <p>{t('patientSearch.offlinePatientSearchUnavailableMessage')}</p>
      </IonLabel>
    </IonItem>
  )

  const clearButton = (
    <IonButton
      data-testid='Clear'
      style={{ background: 'var(--ion-color-light, #f4f5f8)' }}
      color='light'
      expand='block'
      onClick={onClear}>
      {t('patientSearch.clear')}
    </IonButton>
  )

  const searchButton = (
    <IonButton
      className='btn-primary'
      data-testid='Search'
      disabled={!hasMinReqFields()}
      fill='clear'
      expand='block'
      shape='round'
      onClick={onSearch}>
      {t('patientSearch.search')}
    </IonButton>
  )

  return (
    <>
      {!isOfflineStatus && (
        <IonList>
          {isTablet && infoTextItem}

          <IonGrid className='no-padding'>
            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  label={t('patientSearch.firstName')}
                  dataTestID='FirstName'
                  inputValue={firstName}
                  onChangeAction={setFirstNameOnChange}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <SInput
                  isPHI
                  label={t('patientSearch.lastName')}
                  dataTestID='LastName'
                  inputValue={lastName}
                  onChangeAction={setLastNameOnChange}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <NumberPad
                  isPHI
                  elementId='search-DOB'
                  dataTestId='DOB'
                  values={dOB ? dOB.toString().split('') : []}
                  label={t('patientSearch.dOB')}
                  onNumPadClick={setDobOnChange}
                  action={PatientSearchConstants.SET_DOB}
                />
                <Validation id={PatientSearchConstants.SET_DOB} showReduxStateErrorsOnlyFlag={true} />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <NumberPad
                  isPHI
                  elementId='SSN'
                  dataTestId='SSN'
                  values={sSN ? sSN.toString().split('') : []}
                  label={t('patientSearch.sSN')}
                  onNumPadClick={setSsnOnChange}
                  action={PatientSearchConstants.SET_SSN}
                />
                <Validation id={PatientSearchConstants.SET_SSN} showReduxStateErrorsOnlyFlag={true} />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </IonGrid>
          {!isTablet && infoTextItem}
          {!isTablet && searchButton}
          {!isTablet && clearButton}

          {isTablet && (
            <IonRow>
              <IonCol>{clearButton}</IonCol>
              <IonCol>{searchButton}</IonCol>
            </IonRow>
          )}
        </IonList>
      )}
      {isOfflineStatus && offlineInfoTextItem}
      {isOfflineStatus && isTablet && !isFromPulsara && <NewPatientButton />}
    </>
  )
}

export default PatientSearchContent
